import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import MagneticButton from "@/components/MagneticButton";
import SpinningBadge from "@/components/SpinningBadge";

const HERO_BG =
    "https://images.unsplash.com/photo-1756276900419-868625adff43?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxzdHJlZXR3ZWFyJTIwZmFzaGlvbiUyMG1vZGVsJTIwZGFya3xlbnwwfHx8fDE3Nzg3MDQzMzJ8MA&ixlib=rb-4.1.0&q=85";

const TICKER = [
    "// LIVE",
    "DROP 04 · INCOMING",
    "VOLUME LIMITED · NUMBERED",
    "FREE SHIPPING OVER $120",
    "4.9★ · 2,847 VERIFIED BUYERS",
    "ENERGY MATCHER™",
    "—",
];

export default function Hero() {
    const { scrollY } = useScroll();
    const yImage = useTransform(scrollY, [0, 800], [0, 160]);
    const yTitle = useTransform(scrollY, [0, 800], [0, -80]);
    const opacityHero = useTransform(scrollY, [0, 600], [1, 0.4]);

    return (
        <section
            data-testid="hero-section"
            className="relative min-h-[100vh] w-full overflow-hidden bg-black grain"
        >
            {/* Parallax background */}
            <motion.div className="absolute inset-0" style={{ y: yImage, opacity: opacityHero }}>
                <img
                    src={HERO_BG}
                    alt="Energy Matcher Editorial"
                    className="w-full h-full object-cover opacity-50"
                    style={{ filter: "grayscale(40%) contrast(1.1)" }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/20 to-black" />
                {/* Diagonal lines overlay */}
                <svg
                    aria-hidden
                    className="absolute inset-0 w-full h-full opacity-[0.05] mix-blend-overlay"
                    preserveAspectRatio="none"
                    viewBox="0 0 100 100"
                >
                    {[...Array(20)].map((_, i) => (
                        <line
                            key={i}
                            x1={i * 6}
                            y1={0}
                            x2={i * 6 - 30}
                            y2={100}
                            stroke="white"
                            strokeWidth="0.05"
                        />
                    ))}
                </svg>
            </motion.div>

            {/* Top meta strip */}
            <div className="absolute top-20 left-0 right-0 z-10 px-6 lg:px-12 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <span className="w-1.5 h-1.5 bg-signal rounded-full animate-pulse" />
                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/70">
                        Drop 04 — Collection Live
                    </p>
                </div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 hidden sm:block">
                    Drop 04 · Incoming
                </p>
            </div>

            {/* Massive vertical-character right rail */}
            <div className="hidden md:flex absolute top-1/2 -translate-y-1/2 left-6 z-10 flex-col gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 writing-mode-vertical">
                {"//STREETWEAR".split("").map((c, i) => (
                    <span key={i}>{c === "/" ? "·" : c}</span>
                ))}
            </div>

            {/* Massive typography */}
            <motion.div
                className="absolute inset-0 z-10 flex items-end pb-16 lg:pb-24"
                style={{ y: yTitle }}
            >
                <div className="max-w-[1600px] mx-auto w-full px-6 lg:px-20">
                    <motion.div
                        initial={{ opacity: 0, y: 60 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
                    >
                        <h1
                            data-testid="hero-title"
                            className="font-display text-[clamp(3.5rem,15vw,18rem)] leading-[0.78] tracking-[-0.04em] uppercase font-black"
                        >
                            <span className="block relative">
                                Energy
                                <span className="absolute top-0 right-0 hidden md:inline-flex items-start">
                                    <sup className="font-mono text-[10px] tracking-[0.3em] text-white/40 mt-4 mr-2">
                                        ™
                                    </sup>
                                </span>
                            </span>
                            <span className="block text-outline italic font-normal -mt-2 md:-mt-4">
                                Matcher.
                            </span>
                        </h1>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 0.7 }}
                        className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-6 items-end"
                    >
                        <div className="md:col-span-3">
                            <p className="label-mono mb-2">— Statement</p>
                            <p className="font-sans text-sm md:text-base text-white/80 leading-relaxed">
                                Elevated essentials for individuals who move with
                                purpose. <span className="text-white/40">Clothing that matches the energy you bring into every environment.</span>
                            </p>
                        </div>
                        <div className="md:col-span-3 md:col-start-5 hidden md:block">
                            <p className="label-mono mb-2">— Edition</p>
                            <p className="font-display text-3xl leading-none">
                                03 / 06
                            </p>
                            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 mt-2">
                                Limited Production · Sequential Numbering
                            </p>
                        </div>
                        <div className="md:col-span-4 md:col-start-9 flex justify-start md:justify-end">
                            <MagneticButton
                                as="a"
                                href="#shop"
                                data-testid="hero-cta-shop"
                                data-cursor-label="Shop ↓"
                                className="group inline-flex items-center gap-3 px-7 py-4 bg-white text-black hover:bg-white/85 transition-colors font-mono text-xs uppercase tracking-[0.25em]"
                            >
                                Enter the drop
                                <ArrowDownRight
                                    size={16}
                                    className="group-hover:translate-x-0.5 group-hover:translate-y-0.5 transition-transform"
                                />
                            </MagneticButton>
                        </div>
                    </motion.div>
                </div>
            </motion.div>

            {/* Bottom corner mini-ticker (live brand-feed) */}
            <div className="absolute bottom-0 inset-x-0 z-10 border-t border-white/10 bg-black/70 backdrop-blur-sm">
                <div className="overflow-hidden">
                    <div className="flex gap-12 py-3 animate-marquee-h whitespace-nowrap">
                        {[...TICKER, ...TICKER, ...TICKER].map((t, i) => (
                            <span
                                key={i}
                                className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60 shrink-0"
                            >
                                {t}
                            </span>
                        ))}
                    </div>
                </div>
            </div>

            {/* Spinning brand badge — floating top-right (desktop) */}
            <motion.div
                initial={{ opacity: 0, scale: 0.7 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1, delay: 1.2, ease: [0.16, 1, 0.3, 1] }}
                className="hidden lg:block absolute top-32 right-12 z-20"
                data-testid="hero-spinning-badge"
            >
                <SpinningBadge size={180} duration={16} text="ENERGY MATCHER · DROP 04 · ENERGY MATCHER · DROP 04 · " />
            </motion.div>
        </section>
    );
}
